import { ApiConfig } from "../../features/auth/infrastructure/config/apiConfig";
import { authSession } from "./authSession";
import { onSessionTokensChanged } from "./sessionEvents";

const ACCESS_REFRESH_LEAD_MS = 2 * 60 * 1000;
const REFRESH_LOGOUT_LEAD_MS = 24 * 60 * 60 * 1000;
const MAX_TIMEOUT_MS = 2_147_483_647;
const REFRESH_LOCK_KEY = "auth_refresh_lock";
const REFRESH_LOCK_TTL_MS = 8000;

const tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

let accessTimer: number | undefined;
let refreshLogoutTimer: number | undefined;
let refreshInFlight: Promise<boolean> | null = null;
let loggingOut = false;
let started = false;

function jwtExpiryMs(token: string): number | null {
  const part = token.split(".")[1];
  if (!part) return null;
  try {
    const padded = part.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(padded)) as { exp?: unknown };
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function clearTimers(): void {
  window.clearTimeout(accessTimer);
  window.clearTimeout(refreshLogoutTimer);
  accessTimer = undefined;
  refreshLogoutTimer = undefined;
}

export function logoutSession(): void {
  if (loggingOut) return;
  loggingOut = true;
  clearTimers();
  releaseRefreshLock();
  authSession.clearTokens();
  if (!window.location.pathname.startsWith("/login")) {
    window.location.href = "/login";
  }
}

export async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = refreshAccessTokenOnce().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

function accessIsFresh(): boolean {
  const accessToken = authSession.getAccessToken();
  if (!accessToken) return false;
  const expiry = jwtExpiryMs(accessToken);
  if (expiry == null) return false;
  return expiry - Date.now() > ACCESS_REFRESH_LEAD_MS;
}

function readLock(): { owner: string; at: number } | null {
  const raw = localStorage.getItem(REFRESH_LOCK_KEY);
  if (!raw) return null;
  const [owner, at] = raw.split("|");
  const timestamp = Number(at);
  if (!owner || !Number.isFinite(timestamp)) return null;
  return { owner, at: timestamp };
}

function acquireRefreshLock(): boolean {
  const existing = readLock();
  if (
    existing &&
    existing.owner !== tabId &&
    Date.now() - existing.at < REFRESH_LOCK_TTL_MS
  ) {
    return false;
  }
  localStorage.setItem(REFRESH_LOCK_KEY, `${tabId}|${Date.now()}`);
  return readLock()?.owner === tabId;
}

function releaseRefreshLock(): void {
  if (readLock()?.owner === tabId) {
    localStorage.removeItem(REFRESH_LOCK_KEY);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function refreshAccessTokenOnce(): Promise<boolean> {
  if (!authSession.getRefreshToken()) return false;
  if (accessIsFresh()) return true;

  let holdsLock = acquireRefreshLock();
  if (!holdsLock) {
    const started = Date.now();
    while (!holdsLock && Date.now() - started < REFRESH_LOCK_TTL_MS) {
      await sleep(200);
      if (accessIsFresh()) return true;
      holdsLock = acquireRefreshLock();
    }
  }
  if (!holdsLock) return accessIsFresh();

  try {
    if (accessIsFresh()) return true;
    const refreshToken = authSession.getRefreshToken();
    if (!refreshToken) return false;

    const response = await fetch(ApiConfig.REFRESH_TOKEN, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) return false;

    const json = await response.json();
    const payload = json.data ?? json;
    const accessToken: string | undefined = payload.access_token;
    const nextRefreshToken: string | undefined = payload.refresh_token;
    if (!accessToken || !nextRefreshToken) return false;

    authSession.setTokens(accessToken, nextRefreshToken);
    return true;
  } catch {
    return false;
  } finally {
    releaseRefreshLock();
  }
}

export async function ensureAccessTokenFresh(): Promise<boolean> {
  const accessToken = authSession.getAccessToken();
  const refreshToken = authSession.getRefreshToken();
  if (!accessToken || !refreshToken) return true;

  const expiry = jwtExpiryMs(accessToken);
  if (expiry == null) return true;
  if (expiry - Date.now() > ACCESS_REFRESH_LEAD_MS) return true;

  return refreshAccessToken();
}

function scheduleAfter(delayMs: number, run: () => void): number {
  const delay = Math.min(Math.max(delayMs, 0), MAX_TIMEOUT_MS);
  return window.setTimeout(() => {
    if (delayMs > MAX_TIMEOUT_MS) {
      scheduleFromStorage();
      return;
    }
    run();
  }, delay);
}

function scheduleFromStorage(): void {
  if (loggingOut) return;
  clearTimers();

  const accessToken = authSession.getAccessToken();
  const refreshToken = authSession.getRefreshToken();
  scheduleAccessRefresh(accessToken, refreshToken);
  scheduleRefreshLogout(refreshToken);
}

function scheduleAccessRefresh(
  accessToken: string | null,
  refreshToken: string | null,
): void {
  if (!accessToken || !refreshToken) return;
  const expiry = jwtExpiryMs(accessToken);
  if (expiry == null) return;

  const delay = expiry - ACCESS_REFRESH_LEAD_MS - Date.now();
  if (delay <= 0) {
    void refreshAccessToken().then((ok) => {
      if (!ok) logoutSession();
    });
    return;
  }

  accessTimer = scheduleAfter(delay, () => {
    void refreshAccessToken().then((ok) => {
      if (!ok) logoutSession();
    });
  });
}

function scheduleRefreshLogout(refreshToken: string | null): void {
  if (!refreshToken) return;
  const expiry = jwtExpiryMs(refreshToken);
  if (expiry == null) return;

  const now = Date.now();
  const logoutAt = expiry - REFRESH_LOGOUT_LEAD_MS;
  const effectiveLogoutAt = logoutAt > now ? logoutAt : expiry;
  const delay = effectiveLogoutAt - now;
  if (delay <= 0) {
    logoutSession();
    return;
  }

  refreshLogoutTimer = scheduleAfter(delay, () => logoutSession());
}

export function startSessionRefresh(): void {
  scheduleFromStorage();
  if (started) return;
  started = true;
  onSessionTokensChanged(scheduleFromStorage);
  window.addEventListener("storage", (event) => {
    if (event.key !== "access_token" && event.key !== "refresh_token") return;
    if (event.key === "access_token" && !event.newValue) {
      clearTimers();
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
      return;
    }
    scheduleFromStorage();
  });
}
